import { supabase } from './supabase.js'

class StorageService {
  constructor() {
    console.log('💾 Storage Service initialized')
    this.bucketName = 'user-images'
  }

  /**
   * Get user's storage usage and quota information
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Storage usage data
   */
  async getStorageUsage(userId) {
    try {
      const { data, error } = await supabase
        .from('user_storage_usage')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') { // Not found error
        throw error
      }

      // If no record exists, return default free plan values
      if (!data) {
        return {
          total_bytes_used: 0,
          image_count: 0,
          quota_bytes: 52428800, // 50MB
          plan_type: 'free',
          usage_percentage: 0,
          remaining_bytes: 52428800
        }
      }

      const usage_percentage = Math.round((data.total_bytes_used / data.quota_bytes) * 100)
      const remaining_bytes = Math.max(0, data.quota_bytes - data.total_bytes_used)

      return {
        ...data,
        usage_percentage,
        remaining_bytes
      }
    } catch (error) {
      console.error('Error getting storage usage:', error)
      throw error
    }
  }

  /**
   * Check if user can upload a file of given size
   * @param {string} userId - User ID
   * @param {number} fileSize - File size in bytes
   * @returns {Promise<boolean>} Whether upload is allowed
   */
  async canUpload(userId, fileSize) {
    try {
      const { data, error } = await supabase
        .rpc('can_user_upload', {
          p_user_id: userId,
          p_file_size: fileSize
        })

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error checking upload permission:', error)
      return false
    }
  }

  /**
   * Update user's plan type and quota
   * @param {string} userId - User ID
   * @param {string} planType - 'free' or 'pro'
   * @returns {Promise<void>}
   */
  async updateUserPlan(userId, planType) {
    try {
      const { error } = await supabase
        .rpc('update_user_plan', {
          p_user_id: userId,
          p_plan_type: planType
        })

      if (error) throw error
      console.log(`✅ Updated user ${userId} to ${planType} plan`)
    } catch (error) {
      console.error('Error updating user plan:', error)
      throw error
    }
  }

  /**
   * Upload file to Supabase Storage
   * @param {string} userId - User ID
   * @param {File} file - File to upload
   * @param {string} folder - Folder within user's directory
   * @returns {Promise<Object>} Upload result with public URL
   */
  async uploadFile(userId, file, folder = 'uploads') {
    try {
      // Check quota first
      const canUpload = await this.canUpload(userId, file.size)
      if (!canUpload) {
        throw new Error('Storage quota exceeded. Please upgrade your plan or delete some images.')
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `${userId}/${folder}/${fileName}`

      console.log('📤 Uploading file to:', filePath)

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(this.bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(this.bucketName)
        .getPublicUrl(filePath)

      return {
        path: filePath,
        url: urlData.publicUrl,
        size: file.size,
        name: file.name,
        type: file.type
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      throw error
    }
  }

  /**
   * Delete file from Supabase Storage
   * @param {string} filePath - Path to file in storage
   * @returns {Promise<void>}
   */
  async deleteFile(filePath) {
    try {
      const { error } = await supabase.storage
        .from(this.bucketName)
        .remove([filePath])

      if (error) throw error
      console.log('🗑️ File deleted from storage:', filePath)
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error
    }
  }

  /**
   * Generate thumbnail for image
   * @param {string} filePath - Original file path
   * @param {number} width - Thumbnail width
   * @param {number} height - Thumbnail height
   * @returns {Promise<string>} Thumbnail URL
   */
  getThumbnailUrl(filePath, width = 300, height = 300) {
    const { data } = supabase.storage
      .from(this.bucketName)
      .getPublicUrl(filePath, {
        transform: {
          width,
          height,
          resize: 'cover',
          quality: 80
        }
      })

    return data.publicUrl
  }

  /**
   * Format bytes to human readable string
   * @param {number} bytes - Bytes to format
   * @returns {string} Formatted string
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Get quota information for display
   * @param {string} planType - User's plan type
   * @returns {Object} Quota information
   */
  getQuotaInfo(planType) {
    const quotas = {
      free: {
        bytes: 52428800, // 50MB
        display: '50MB',
        maxImages: '~75-250 images'
      },
      pro: {
        bytes: 262144000, // 250MB
        display: '250MB',
        maxImages: '~375-1,250 images'
      }
    }

    return quotas[planType] || quotas.free
  }

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @returns {Object} Validation result
   */
  validateFile(file) {
    const maxSize = 10 * 1024 * 1024 // 10MB per file
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml'
    ]

    const errors = []

    if (!allowedTypes.includes(file.type)) {
      errors.push('File type not supported. Please use JPEG, PNG, GIF, WebP, or SVG.')
    }

    if (file.size > maxSize) {
      errors.push(`File too large. Maximum size is ${this.formatBytes(maxSize)}.`)
    }

    if (file.size === 0) {
      errors.push('File is empty.')
    }

    return {
      valid: errors.length === 0,
      errors
    }
  }
}

// Create and export singleton instance
export const storageService = new StorageService()
